import moment from "moment";

import { ISortableItemRenderFunction } from "@/Common/Components/Collections/SortableItemContainer";
import { PlusIconButton } from "@/Common/Components/IconControls/PlusIconButton/PlusIconButton";
import { Modal } from "@/Common/Components/Modals/Modal";
import { SearchBoxControl } from "@/Common/Components/Search/SearchBoxControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { HighlightHelper } from "@/Common/Helpers/HighlightHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { OpenAttachmentsPopupItem } from "@/Common/Models/Attachments/OpenAttachmentsPopupItem";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { Comment } from "@/Common/Models/Patient/Comment";
import { Patient } from "@/Common/Models/Patient/Patient";
import { MedicalItemsTab, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import { HighlightService } from "@/Common/Models/Services/HighlightService";
import { SortableItemUIModel } from "@/Common/Models/UI/SortableItemUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { Relationship } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { Tag } from "@/DesignSystem";
import { ExportableFileDispatcher } from "@/ExportableFile/Helpers/ExportableFileDispatcher";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { EncountersSidePanel } from "@/Patient/Components/Common/EncountersSidePanel";
import { QuebecVaccinationRegistryHelper } from "@/Patient/Components/MedicalItems/Immunization/QuebecVaccinationRegistryHelper";
import { NewMedicalNoteNoSpecialityWarningPopup } from "@/Patient/Components/MedicalNote/NewMedicalNoteNoSpecialityWarningPopup";
import { NewMedicalNoteWarningPopup } from "@/Patient/Components/MedicalNote/NewMedicalNoteWarningPopup";
import { AlertPopup } from "@/Patient/Components/MedicalSummary/AlertPopup";
import { ObstetricalSummaryComponent } from "@/Patient/Components/MedicalSummary/ObstetricalSummaryComponent";
import { ObstetricalSummaryPopup } from "@/Patient/Components/MedicalSummary/ObstetricalSummaryPopup";
import { SortableContainerForActiveMedication } from "@/Patient/Components/MedicalSummary/SortableContainerForActiveMedication";
import { SortableContainerForAlerts } from "@/Patient/Components/MedicalSummary/SortableContainerForAlerts";
import { SortableContainerForImmunizations } from "@/Patient/Components/MedicalSummary/SortableContainerForImmunizations";
import { SortableContainerForProblemsAndContitions } from "@/Patient/Components/MedicalSummary/SortableContainerForProblemsAndContitions";
import { SortableContainerForRiskFactors } from "@/Patient/Components/MedicalSummary/SortableContainerForRiskFactors";
import { MedicalSummarySections } from "@/Patient/Components/MedicalSummaryConfiguration/MedicalSummaryEnum";
import { LongTermMedicationIcon } from "@/Patient/Components/Medication/LongTermMedicationIcon";
import { ScreeningTestWarningIcon } from "@/Patient/Components/ScreeningTest/ScreeningTestWarningIcon";
import { VitalSignsView } from "@/Patient/Components/VitalSigns/VitalSignsView";
import { MedicalItemHelper } from "@/Patient/Helpers/MedicalItemHelper";
import { MedicalItemRiskFactorHelper } from "@/Patient/Helpers/MedicalItemRiskFactorHelper";
import { MedicalNotesConfigurationHelper } from "@/Patient/Helpers/MedicalNotesConfigurationHelper";
import { MedicalSummaryConfigurationHelper } from "@/Patient/Helpers/MedicalSummaryConfigurationHelper";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { VitalSignPercentileUtility } from "@/Patient/Helpers/VitalSignPercentileUtility";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { FollowUpItem } from "@/Patient/Models/MedicalSummary/FollowUpItem";
import { FollowUpTableItemModel } from "@/Patient/Models/MedicalSummary/FollowUpTableItemModel";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { EnumPatientSummaryMedicationDisplayStatus } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationDisplayStatus";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { ObstetricalSummary } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { FollowUpResponse } from "@/Patient/Models/MedicalSummary/Response/FollowUpResponse";
import { ScreeningTestTableItemModel } from "@/Patient/Models/MedicalSummary/ScreeningTestTableItemModel";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { NotesConfigurationModel } from "@/Patient/Models/NotesConfigurationModel";
import { SummaryLayoutItem } from "@/Patient/Models/SummaryLayoutItem";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { MedicalRequestPopupController } from "@/Portfolio/Components/MedicalRequestForm/MedicalRequestPopupController";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { PrintInfo } from "@/Portfolio/Models/PrintInfo";

import styles from "./MedicalSummary.module.scss";

import Printer from "@/DesignSystem/Atoms/Icons/Types/Printer/printer-regular.svg";
import img_icon_attachment_icon from "@/Globals/_images/icons/attachment_icon.png";

interface IMedicalSummaryProps {
    model: IPatientMedicalSummaryModel;
    currentPatient: Patient;
    isReadOnly?: boolean;
    isHighlighting?: boolean;
    displayOnlyApprovedEncounters?: boolean;
    summaryConfiguration: SummaryLayoutItem[];
    onRequestDeletedSuccess: (requestId: string, encounterId: Guid) => void;
    onConsultationRequestDeletedSuccess: (consultationRequestId: string, encounterId: Guid) => void;
    isPatientNoteDisabled: boolean;
    onMedicalItemClick: (item: MedicalItem, view: MedicalItemsTab) => void;
    onSwitchView: (item: MedicalItem, view: MedicalItemsTab) => void;
    onEncounterClick: (encounterId: string) => void;
    onActiveMedicationDetailsClick: () => void;
    onNewNote: () => void;
    onLabRequestSaveSuccess: (model: IMedicalRequest, shouldSaveRelatedEmployee: boolean, printInfo: PrintInfo) => void;
    onLabRequestToggleIsInvalid: (id: Guid, isInvalid: boolean) => void;
    onConsultationRequestSaveSuccess: (model: IMedicalRequest, shouldSaveRelatedEmployee: boolean, printInfo: PrintInfo) => void;
    onConsultationRequestToggleIsInvalid: (id: Guid, isInvalid: boolean) => void;
    onEncounterDateToChanged: (date: Date) => void;
    onEncounterDateFromChanged: (date: Date) => void;
    onEncounterMyNotesFilterChecked: (checked: boolean) => void;
    onEncounterUnapprovedNotesFilterChecked: (checked: boolean) => void;
    onEncounterClinicVisitNotesFilterChecked: (checked: boolean) => void;
    onChangeEncounterSearchTermChanged: (textOrDate: any) => void;
    onClickCallbackPrintNotes: () => void;
    onShowCriteria: () => void;
    onToggleDisplayOnlyUnsigned: () => void;
    onUpdateScreeningsModelAfterPrintingRequest: (requestId: string, requestType: MedicalRequestType) => void;
    OnSortDoneFunction: (operation: number, itemId: string, itemIndex: number, startGroup: string, endGroup: string) => void;
    onSaveObstetricalSummary: (obstetricalSummary: ObstetricalSummary) => void;
}

class MedicalSummaryState {
    public showCommentPopup: boolean;
    public selectedScreeningTest: MedicalItemScreeningTest;
    public commentsPopupClientX: number;
    public commentsPopupClientY: number;
    public showLabRequestPopup: boolean;
    public showConsultationPopup: boolean;
    public selectedFollowUpId: string;
    public selectedEncounterId: string;
    public showObstetricalSummaryPopup: boolean;
    public componentSummaryConfiguration: SummaryLayoutItem[];
    public sortedVisibleComponents: SummaryLayoutItem[];
    public summaryConfiguration: SummaryLayoutItem[];
    public notesConfiguration: NotesConfigurationModel;

    public percentiles: { [index in VitalProperty]?: string };

    public latestVitalSign: MedicalItemVitalSign;

    public allElementsToHighlight: DOMRect[];

    public static getDefault(): MedicalSummaryState {
        let s = new MedicalSummaryState();
        s.showCommentPopup = false;
        s.selectedScreeningTest = null;
        s.commentsPopupClientX = 0;
        s.commentsPopupClientY = 0;
        s.showLabRequestPopup = false;
        s.showConsultationPopup = false;
        s.selectedFollowUpId = null;
        s.selectedEncounterId = null;
        s.showObstetricalSummaryPopup = false;
        s.latestVitalSign = new MedicalItemVitalSign();
        s.allElementsToHighlight = [];
        s.summaryConfiguration = [];
        s.sortedVisibleComponents = [];
        s.notesConfiguration = new NotesConfigurationModel();
        return s;
    }
}

class MedicalSummary extends React.Component<IMedicalSummaryProps, MedicalSummaryState> {
    private vitalSignPercentileUtility: VitalSignPercentileUtility = null;
    private medicalSummaryScrollerDivElement: HTMLDivElement = null;
    private highlightService: HighlightService = null;
    private medicalSummaryContainerClassName: string = "medical-summary-container-to-highlight";

    public constructor(props: IMedicalSummaryProps) {
        super(props);

        this.state = MedicalSummaryState.getDefault();
    }

    public render(): JSX.Element {
        const model = this.props.model;
        if (model == null)
            return <div />;

        const shouldShowAlertPopup = model.get("ShowAlertPopup");

        let newNoteWarningPopupComponent: JSX.Element = null;
        if (model.get("ShowNewNoteWarning") === true) {
            newNoteWarningPopupComponent = <NewMedicalNoteWarningPopup SummaryModel={model} />;
        }

        let newNoteWarningNoSpecialityPopupComponent: JSX.Element = null;
        if (model.get("ShowNoteCreationSpecialityErrorWarning") === true) {
            newNoteWarningNoSpecialityPopupComponent = <NewMedicalNoteNoSpecialityWarningPopup SummaryModel={model} />;
        }

        let selectedEncounter: Encounter;
        if (this.state.selectedEncounterId != null)
            selectedEncounter = model.get("Encounters").toArray().firstOrDefault(x => x.Id == this.state.selectedEncounterId);

        const popupWindowWidth = 605;
        const buttonHeightClearance = 10;
        const xPos: number = this.state.commentsPopupClientX - popupWindowWidth;
        const yPos: number = $(".myle-nav")?.position()?.top + this.state.commentsPopupClientY + buttonHeightClearance;
        const emrTemplateId = this.state.selectedScreeningTest ? this.state.selectedScreeningTest.model.EMRTemplateId : "";
        let emrTemplateComments: Comment[] = [];

        if (this.props.model.get("EMRTemplateComments") && this.props.model.get("EMRTemplateComments")[emrTemplateId]) {
            emrTemplateComments = this.props.model.get("EMRTemplateComments")[emrTemplateId].sortByProperty(c => c.CreateDate);
        }

        return (
            <div className="medical-summary-container medical-summary-section" data-cy="medicalSummary">
                <div className="tb medical-summary summary-encounters-list-container">
                    {!this.props.isReadOnly && (
                        <EncountersSidePanel
                            model={model}
                            noteModel={null}
                            isPatientNoteDisabled={this.props.isPatientNoteDisabled}
                            displayOnlyApprovedEncounters={this.props.displayOnlyApprovedEncounters}
                            displayPrintButton={true}
                            isReadOnly={this.props.isReadOnly}
                            toggleDisplayOnlyUnsigned={() => this.props.onToggleDisplayOnlyUnsigned()}
                            showCriteria={() => this.props.onShowCriteria()}
                            onClickCallbackPrintNotes={() => this.props.onClickCallbackPrintNotes()}
                            newNote={() => this.props.onNewNote()}
                            onEncounterClick={encounterId => this.props.onEncounterClick(encounterId)}
                            onEncounterSearchTermChanged={textOrDate => this.props.onChangeEncounterSearchTermChanged(textOrDate)}
                            fromChange={date => this.props.onEncounterDateFromChanged(date)}
                            toChange={date => this.props.onEncounterDateToChanged(date)}
                            onSummaryConfigurationChange={() => this.updateSummaryConfiguration()}
                            onNotesConfigurationChange={() => this.updateNotesConfiguration()}
                            onMyNotesFilterChanged={(checked => this.props.onEncounterMyNotesFilterChecked(checked))}
                            onClinicVisitNotesFilterChanged={(checked => this.props.onEncounterClinicVisitNotesFilterChecked(checked))}
                        />
                    )}
                    {this.RenderMedicalSummaryColumn()}
                </div>
                {shouldShowAlertPopup &&
                    <AlertPopup Model={this.props.model} />
                }

                {this.state.showObstetricalSummaryPopup && this.renderObstetricalSummaryPopup()}

                {newNoteWarningPopupComponent}
                {newNoteWarningNoSpecialityPopupComponent}
                {this.state.showCommentPopup && (
                    <Modal
                        title={this.state.selectedScreeningTest?.getScreeningTestTitle() ?? ""}
                        left={xPos}
                        top={yPos}
                        onClose={() => this.mutateState(s => s.showCommentPopup = false)}
                    >
                        <div style={{ maxHeight: "300px" }}>
                            {this.renderCommentsBody(emrTemplateComments)}
                        </div>
                    </Modal>
                )}
                {this.state.showLabRequestPopup && (
                    <MedicalRequestPopupController
                        type={MedicalRequestType.LabRequest}
                        id={this.state.selectedFollowUpId}
                        encounter={{
                            id: this.state.selectedEncounterId,
                            consultationDate: selectedEncounter.ConsultationDate,
                            isSignedOff: selectedEncounter.IsSignedOff,
                            SignedByEmployeeId: selectedEncounter.SignedOffByEmployeeId,
                            SignedOffDate: selectedEncounter.SignedOffDate
                        }}
                        siteId={selectedEncounter.SiteId}
                        patientDoB={this.props.currentPatient?.IndividualResponse?.DateOfBirth}
                        closeAfterButtonClick={true}
                        onClose={() => this.closeRequestPopup()}
                        onSaveSuccess={(model, shouldSaveRelatedEmployee: boolean, printInfo) =>
                            this.props.onLabRequestSaveSuccess(model, shouldSaveRelatedEmployee, printInfo)
                        }
                        onDeleteSuccess={(requestId, encounterId) => this.props.onRequestDeletedSuccess(requestId, encounterId)}
                        onPrintSuccess={(fileName, requestId) => this.onMedicalRequestPrintSuccess(fileName, requestId, MedicalRequestType.LabRequest)}
                        onToggleIsInvalid={(id, _, isInvalid) => this.props.onLabRequestToggleIsInvalid(id, isInvalid)}
                        onSignClick={modelLabRequest => this.onLabRequestSignClick(modelLabRequest)}
                        patientContactIdForNewRequests={this.props.currentPatient.IndividualResponse.Contact_id}
                    />
                )}

                {this.state.showConsultationPopup && (
                    <MedicalRequestPopupController
                        type={MedicalRequestType.ConsultationRequest}
                        id={this.state.selectedFollowUpId}
                        encounter={{
                            id: this.state.selectedEncounterId,
                            consultationDate: selectedEncounter.ConsultationDate,
                            isSignedOff: selectedEncounter.IsSignedOff,
                            SignedByEmployeeId: selectedEncounter.SignedOffByEmployeeId,
                            SignedOffDate: selectedEncounter.SignedOffDate
                        }}
                        patientDoB={this.props.currentPatient?.IndividualResponse?.DateOfBirth}
                        closeAfterButtonClick={true}
                        onClose={() => this.closeRequestPopup()}
                        onSaveSuccess={(model, shouldSaveRelatedEmployee, printInfo) =>
                            this.props.onConsultationRequestSaveSuccess(model, shouldSaveRelatedEmployee, printInfo)
                        }
                        onDeleteSuccess={(requestId, encounterId) => this.props.onConsultationRequestDeletedSuccess(requestId, encounterId)}
                        onPrintSuccess={(fileName, requestId) => this.onMedicalRequestPrintSuccess(fileName, requestId, MedicalRequestType.ConsultationRequest)}
                        onToggleIsInvalid={(id, _, isInvalid) => this.props.onConsultationRequestToggleIsInvalid(id, isInvalid)}
                        onSignClick={modelConsultationRequest => this.onConsultationSignClick(modelConsultationRequest)}
                        patientContactIdForNewRequests={this.props.currentPatient.IndividualResponse.Contact_id}
                    />
                )}

            </div>
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        this.setLatestVitalSign();

        if (this.props?.isHighlighting && this.medicalSummaryScrollerDivElement && !this.highlightService) {
            this.initializeHighlightService();
            this.executeHighlightService(this.props.model?.get("EncounterSearchTerm"), true);
        }
    }

    public componentWillUnmount(): void {
        if (this.props?.isHighlighting) {
            this.terminateHighlightService();
        }
    }

    private setLatestVitalSign(): void {
        if (!this.props.model)
            return;

        this.mutateState(s => {
            s.latestVitalSign = MedicalItemHelper.getLatestVitalSign(this.props.model.get("MedicalItemsVitalSign"));
        }, () => this.calculateAllPercentiles(this.state.latestVitalSign));
    }

    public componentDidUpdate(prevProps: IMedicalSummaryProps): void {
        $(window).trigger("resize");

        if (this.props.model !== prevProps.model) {
            this.setLatestVitalSign();
        }

        if (this.props?.isHighlighting) {
            if (this.props.currentPatient?.PatientResponse?.ContactFacet_id != prevProps.currentPatient?.PatientResponse?.ContactFacet_id) {
                this.updateSummaryConfiguration();
                this.updateNotesConfiguration();

                this.terminateHighlightService();
            }

            if (this.highlightService) {
                if (
                    prevProps?.currentPatient?.IndividualResponse?.Contact_id !== this.props?.currentPatient?.IndividualResponse?.Contact_id ||
                    prevProps.model?.get("EncounterSearchTerm") != this.props.model?.get("EncounterSearchTerm")
                ) {
                    this.executeHighlightService(this.props.model?.get("EncounterSearchTerm"), true);
                }
            }
            else {
                if (this.medicalSummaryScrollerDivElement) {
                    this.terminateHighlightService();
                    this.initializeHighlightService();
                    this.executeHighlightService(this.props.model?.get("EncounterSearchTerm"), true);
                }
            }

            if (
                this.highlightService?.allElementsToHighlight &&
                this.state.allElementsToHighlight &&
                CommonHelper.isDOMRectArraysNotEqual(this.highlightService?.allElementsToHighlight, this.state.allElementsToHighlight)
            ) {
                this.executeHighlightService(this.props.model?.get("EncounterSearchTerm"), false);
            }

            if (!this.state.componentSummaryConfiguration || prevProps.summaryConfiguration !== this.props.summaryConfiguration) {
                if (!this.state.componentSummaryConfiguration)
                    this.setSummaryComponentsPosition(true);
                else {
                    const curHidden = Object.fromEntries(this.props.summaryConfiguration.map(config => [config.Section, config.IsHidden]));
                    this.setSummaryComponentsPosition(
                        prevProps.summaryConfiguration.any(prevConfig => prevConfig.IsHidden !== curHidden[prevConfig.Section]) // hidden flag changed
                    );
                }
            }
        }
    }

    private setSummaryComponentsPosition(setComponents: boolean): void {
        const summaryConfigGrid = cacheStore.ClinicSettings.EnableSummaryConfiguration
            ? MedicalSummaryConfigurationHelper.getSpecificOrDefaultSummaryConfigurationGridLayout()
            : MedicalSummaryConfigurationHelper.getOriginalSummaryConfigurationGridLayout();

        const visibleGridItems = summaryConfigGrid.filter(summaryItem => !summaryItem.IsHidden);
        const sortedVisibleComponents = visibleGridItems.sortByProperty(componentSummaryColumnSort => componentSummaryColumnSort.Column);

        this.mutateState(s => {
            if (setComponents) {
                s.componentSummaryConfiguration = visibleGridItems;
            }
            s.sortedVisibleComponents = sortedVisibleComponents;
        });
    }

    private RenderMedicalSummaryColumn(): JSX.Element {
        const scrollWidth = (this.medicalSummaryScrollerDivElement?.offsetWidth ?? 0) - (this.medicalSummaryScrollerDivElement?.clientWidth ?? 0);

        return (
            <div
                className={`tbc right-primary medical-summary-main-container ${this.props?.isHighlighting ? this.medicalSummaryContainerClassName : ""}` + (this.props.isReadOnly ? " no-hover" : "")}
            >
                {this.props?.isHighlighting
                    && !IsNullOrWhiteSpace(this.props.model?.get("EncounterSearchTerm") ?? "")
                    && !isArrayNullOrUndefinedOrEmpty(this.state.allElementsToHighlight)
                    && (
                        <SearchBoxControl
                            key={this.props.currentPatient?.IndividualResponse?.Contact_id}
                            searchText={this.props.model?.get("EncounterSearchTerm") ?? ""}
                            container={this.medicalSummaryScrollerDivElement}
                            elementsRects={this.state.allElementsToHighlight ?? []}
                            currentScrollTop={this.highlightService?.currentScrollTop ?? 0}
                            rightInPX={scrollWidth + 17}
                            encounterSearchTermChangedOperation={LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged}
                            setHighlightServiceIndexCallbackFunc={(curIndex: number) => {
                                if (this.highlightService) {
                                    this.highlightService.index = curIndex;
                                    this.highlightService.execute(null, false);
                                }
                            }}
                            scrollToRatio={2}
                        />
                    )
                }
                <div
                    id="medicalSummaryScrollerContainer"
                    className={"scrollable " + (this.props.isHighlighting ? "highlight-container" : "")}
                    data-hvar="60"
                    ref={ref => this.medicalSummaryScrollerDivElement = ref}
                >
                    {this.renderConditionalSummary()}
                </div>
                {this.RenderVitalSigns()}
            </div>
        );
    }

    private renderConditionalSummary(): JSX.Element {
        if (cacheStore.ClinicSettings.EnableSummaryConfiguration) {
            const flexHierarchy: JSX.Element[] = [];
            let leftItems: JSX.Element[] = [];
            let rightItems: JSX.Element[] = [];

            this.state.sortedVisibleComponents?.forEach((item: SummaryLayoutItem, index: number) => {
                const currentSectionKey = `${item.Section}-${index}`;

                if (item.Width === 2) {
                    if (leftItems.length || rightItems.length) {
                        flexHierarchy.push(
                            <div key={`container-${currentSectionKey}-${GuidHelper.NewGuid()}`} className={`${styles.sectionSmallSpacing} flex-container`}>
                                <div className={styles.summaryItemSection}>
                                    {leftItems}
                                </div>
                                <div className={styles.summaryItemSection}>
                                    {rightItems}
                                </div>
                            </div>
                        );
                        leftItems = [];
                        rightItems = [];
                    }
                    flexHierarchy.push(
                        <div key={`full-width-container-${currentSectionKey}`} className="width-100">
                            {this.renderSummaryComponent(item)}
                        </div>
                    );
                }
                else {
                    const itemKey = item.Row === 0 ? "left" : "right";
                    const currentItems = item.Row === 0 ? leftItems : rightItems;

                    currentItems.push(
                        <div key={`${itemKey}-${currentSectionKey}`}>
                            {this.renderSummaryComponent(item)}
                        </div>
                    );
                }
            });

            if (leftItems.length || rightItems.length) {
                flexHierarchy.push(
                    <div key={`final-row-container-${GuidHelper.NewGuid()}`} className={`${styles.sectionSmallSpacing} flex-container`}>
                        <div className={styles.summaryItemSection}>
                            {leftItems}
                        </div>
                        <div className={styles.summaryItemSection}>
                            {rightItems}
                        </div>
                    </div>
                );
            }

            return (
                <div className={`${styles.sectionMediumSpacing} flex-column form-row`}>
                    {flexHierarchy}
                </div>
            );
        }

        const medicalItems = { top: [], left: [], right: [] };
        for (const config of (this.state.componentSummaryConfiguration ?? [])) {
            if (config.Width === 2)
                medicalItems.top.push(config);
            else if (config.Row === 0)
                medicalItems.left.push(config);
            else
                medicalItems.right.push(config);
        }

        return (
            <div className={styles.medicalSummaryBlock}>
                <div className={styles.medicalSummaryFlex} style={{ marginBottom: 10 }}>
                    {medicalItems.top.map(item => this.renderSummaryComponent(item))}
                </div>
                <div className={styles.medicalSummaryGrid} style={{ padding: 0, alignItems: "baseline" }}>
                    <div className={styles.medicalSummaryFlex}>
                        {medicalItems.left.map(item => this.renderSummaryComponent(item))}
                    </div>
                    <div className={styles.medicalSummaryFlex}>
                        {medicalItems.right.map(item => this.renderSummaryComponent(item))}
                    </div>
                </div>
            </div>
        );
    }

    private renderSortableScreeningTests(): JSX.Element {
        const saveOptionKey = "ScreeningTestTable";
        const tableColumnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getScreeningsMyleTableColumnsDefaultOptions());
        const latestScreeningsToDisplay: MedicalItemScreeningTest[] = MedicalItemScreeningTest.getLatestScreeningsToDisplay(this.props.model);
        const cssClass: string = "bold-header-columns ";

        return (
            <div>
                <MyleTable
                    TableRenderOptions={MyleTable.LoadDefaultTableRenderOptions(tableColumnsOptions)}
                    Columns={this.getScreeningsTableColumns(tableColumnsOptions)}
                    TableId="ScreeningTestsTable"
                    DataSource={this.getScreeningTestsAsTableItems(latestScreeningsToDisplay)}
                    RowClickFunction={item => this.props.onMedicalItemClick(item.Model.getMedicalItem(), MedicalItemsTab.ScreeningTest)}
                    SaveOptionskeyTarget={saveOptionKey}
                    NoScrollLimit
                    HideFooter
                    CssClass={cssClass}
                    ShowHeaderTextInBold
                    UnclickableColumnIndex={6}
                />
            </div>
        );
    }

    private renderSortableFollowUps(): JSX.Element {
        let saveOptionKey = "FollowUpsTable";
        let tableColumnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getFollowUpMyleTableColumnsDefaultOptions());
        let followUpsToDisplay: FollowUpItem[] = this.getFollowUpsToDisplay();
        let cssClass: string = "bold-header-columns ";

        return (
            <div>
                <MyleTable
                    TableRenderOptions={MyleTable.LoadDefaultTableRenderOptions(tableColumnsOptions)}
                    Columns={this.getFollowUpTableColumns(tableColumnsOptions)}
                    TableId="FollowUpsTable"
                    DataSource={this.getFollowUpsAsTableItems(followUpsToDisplay)}
                    RowClickFunction={item => this.onFollowUpItemClick(item.Model)}
                    SaveOptionskeyTarget={saveOptionKey}
                    NoScrollLimit
                    HideFooter
                    CssClass={cssClass}
                    ShowHeaderTextInBold
                    TableDataCy="medicalSummary-followUps"
                />
            </div>
        );
    }

    private getScreeningTestsAsTableItems(screeningTests: MedicalItemScreeningTest[]): Immutable.List<ScreeningTestTableItemModel> {
        if (isArrayNullOrUndefinedOrEmpty(screeningTests))
            return;

        return screeningTests.map(x => new ScreeningTestTableItemModel(x)).toImmutable();
    }

    private getFollowUpsAsTableItems(followUps: FollowUpItem[]): Immutable.List<FollowUpTableItemModel> {
        if (isArrayNullOrUndefinedOrEmpty(followUps))
            return;

        return followUps.map(x => new FollowUpTableItemModel(x)).toImmutable();
    }

    private RenderSortableMedicalProblem(items: MedicalItemProblemAndCondition[], containerName: string, reorderOp: LocalPatientOperations.MedicalSummary): JSX.Element {
        const self = this;
        let sortableProblems: SortableItemUIModel<MedicalItemProblemAndCondition>[] = [];

        const groupedItems = items.groupBy(item => item.EMRTemplateId);

        // build sortableitems and sort them
        groupedItems.forEach(x => {
            const sortable = new SortableItemUIModel<MedicalItemProblemAndCondition>();
            const latestProblem = MedicalItemModel.sortByCreationDateDesc(x).firstOrDefault() as MedicalItemProblemAndCondition;

            sortable.ItemId = latestProblem.EMRTemplateId;
            sortable.ItemIndex = (latestProblem.EMRTemplateSortIndex == null) ? 0 : latestProblem.EMRTemplateSortIndex;
            sortable.Model = latestProblem;
            sortableProblems.push(sortable);
        });

        sortableProblems = ArrayHelper.SortDynamic(sortableProblems, "ItemIndex", false);

        // define the rendering function
        const sortableMedicalProblemRenderFunction: ISortableItemRenderFunction<MedicalItemProblemAndCondition> = function(medicalProblem: MedicalItemProblemAndCondition): JSX.Element {
            let lifeStageRendered = false;
            const diagnosisesRender: JSX.Element[] = [];

            if (medicalProblem.QualifiedDetails != null && medicalProblem.QualifiedDetails.length > 0) {
                medicalProblem.QualifiedDetails.forEach((qualifiedDetail, idx: number) => {
                    let lifeStageString = "";
                    let statusString = "";

                    // get onset date string
                    if (lifeStageRendered == false) {
                        lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(medicalProblem);
                        lifeStageRendered = true;
                    }

                    if (typeof qualifiedDetail.StatusLookupId !== "undefined" && qualifiedDetail.StatusLookupId !== null)
                        statusString = "(" + cacheStore.GetLocalizedValueByLookupId(qualifiedDetail.StatusLookupId) + ")";

                    const isSurg = !(medicalProblem.EMRTemplateMedicalItemSubType == null) && medicalProblem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast;
                    const hasProcedure = !(medicalProblem.Procedure == null) && medicalProblem.Procedure.trim().length > 0;

                    // here we need to render a diagnoses slightly differently if diplaying as a surgical history item with a procedure (slightly indented)
                    if (!isSurg || !hasProcedure) {
                        diagnosisesRender.push(
                            <div className="flex-container" key={qualifiedDetail.Id + idx}>
                                <div className="flx-col a">
                                    {settingsStore.configuration.get("ShowDiagnosisCode") === true && <strong>{qualifiedDetail.Code} - </strong>}
                                    {idx > 0 && <strong> vs: </strong>}
                                    <strong>{qualifiedDetail.Text}</strong>
                                    <i> {statusString}</i>
                                </div>
                                <div className="flx-col b">
                                    <strong>{lifeStageString}</strong>
                                </div>
                            </div>
                        );
                    }
                    else {
                        diagnosisesRender.push(
                            <div className="flex-container" key={qualifiedDetail.Id + idx}>
                                <div className="indent" />
                                <div className="flx-col a">
                                    {settingsStore.configuration.get("ShowDiagnosisCode") === true && <strong>{qualifiedDetail.Code} - </strong>}
                                    {idx > 0 && <strong> vs: </strong>}
                                    <strong>{qualifiedDetail.Text}</strong>
                                    <i> {statusString}</i>
                                </div>
                                <div className="flx-col b">
                                    <strong>{lifeStageString}</strong>
                                </div>
                            </div>
                        );
                    }
                });
            }

            const isClassifiedAsSurgeryHistory = !(medicalProblem.EMRTemplateMedicalItemSubType == null) && medicalProblem.EMRTemplateMedicalItemSubType == MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast;
            const hasDescription = !(medicalProblem.ProblemDescription == null) && medicalProblem.ProblemDescription.trim().length > 0;
            const hasProcedure = !(medicalProblem.Procedure == null) && medicalProblem.Procedure.trim().length > 0;
            const surgeryDiagnosisStyle = hasProcedure ? { paddingTop: "15px" } : null;

            return (
                <div key={"medicalProblem_" + medicalProblem.Id} className="medical-summary-problem" onClick={self.props.onMedicalItemClick.bind(self, medicalProblem, MedicalItemsTab.MedicalProblem)}>

                    {/* --- we will show diagnosis and descriptin at bottom if we have a surgical item and resolved procedure */}
                    {(isClassifiedAsSurgeryHistory == false || !hasProcedure) && <div>{diagnosisesRender}</div>}

                    {(isClassifiedAsSurgeryHistory == false && hasDescription || !hasProcedure) && MedicalSummaryConfigurationHelper.getProblemsDescription() && (
                        <div className="flex-container">
                            <span className="text-gray"> {JSXHelper.BreakStringIntoParagraphs(medicalProblem.ProblemDescription, true)}</span>
                        </div>
                    )}

                    {hasProcedure && (
                        <div className="flex-container">
                            <div className="flx-col a">
                                <div>
                                    <div className="indent"> <strong> CH </strong> </div>
                                    <div className="afterIndent"> <span> <strong> {JSXHelper.BreakStringIntoParagraphs(medicalProblem.Procedure, true)} </strong> </span> </div>
                                </div>
                            </div>
                            <div className="flx-col b">
                                <span> <strong> {PatientMedicalSummaryHelper.GetDateDisplayAccordingToPrecision(medicalProblem.ProcedureDate, medicalProblem.PrecisionDateProcedure)}</strong> </span>
                            </div>
                        </div>
                    )}

                    {/* --- diagnosis at bottom if we have a surgical item */}
                    {isClassifiedAsSurgeryHistory && hasProcedure && (
                        <div style={surgeryDiagnosisStyle}>
                            {diagnosisesRender}
                        </div>
                    )}
                    {isClassifiedAsSurgeryHistory && hasProcedure && hasDescription && (
                        <div className="flex-container">
                            <div className="flx-col a">
                                <div>
                                    <div className="indent"> &nbsp; &nbsp; </div>
                                    <div className="afterIndent"> <span className="text-gray"> {JSXHelper.BreakStringIntoParagraphs(medicalProblem.ProblemDescription, true)}</span>  </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        return (
            <SortableContainerForProblemsAndContitions
                ComponentId={containerName}
                DragGroupClass="problemsAndConditions"
                ItemRenderingFunction={sortableMedicalProblemRenderFunction}
                SortableItems={Immutable.List(sortableProblems)}
                OnSortDoneFunction={(itemId: string, itemIndex: number, startGroup: string, endGroup: string) =>
                    this.props.OnSortDoneFunction(reorderOp, itemId, itemIndex, startGroup, endGroup)
                }
                DragStartDelegate={this.dragStartForMultipleDropZones}
                DragStopDelegate={this.dragStopForMultipleDropZones}
            />
        );
    }

    private RenderSortableOngoingProblemsAndConditions(): JSX.Element {
        // initial filter for displayable active problems
        let problems = this.props.model.get("MedicalItemsProblemsOnGoing").toArray();
        problems = problems.filter(x => x.EMRTemplateIsVisibleInCPP == true && x.EMRTemplateIsInvalid == false);

        return this.RenderSortableMedicalProblem(problems, "problems_ongoing", LocalPatientOperations.MedicalSummary.ReorderMedicalItemProblems);
    }

    private RenderSortablePastProblemsAndConditions(): JSX.Element {
        // initial filter for displayable problems
        let problems = this.props.model.get("MedicalItemsProblemsHistory").toArray();
        problems = problems.filter(x => x.EMRTemplateIsVisibleInCPP == true && x.EMRTemplateIsInvalid == false);

        return this.RenderSortableMedicalProblem(problems, "problems_historical", LocalPatientOperations.MedicalSummary.ReorderMedicalItemProblems);
    }

    private RenderSortableSurgicalHistory(): JSX.Element {
        // initial filter for displayable problems
        let problems = this.props.model.get("MedicalItemsSurgeryHistory").toArray();
        problems = problems.filter(x => x.EMRTemplateIsVisibleInCPP == true && x.EMRTemplateIsInvalid == false);

        return this.RenderSortableMedicalProblem(problems, "problems_surgical", LocalPatientOperations.MedicalSummary.ReorderMedicalItemProblems);
    }

    private RenderSortablePsychiatricHistory(): JSX.Element {
        // initial filter for displayable problems
        let problems = this.props.model.get("MedicalItemsPsychiatricHistory").toArray();
        problems = problems.filter(x => x.EMRTemplateIsVisibleInCPP == true && x.EMRTemplateIsInvalid == false);

        return this.RenderSortableMedicalProblem(problems, "problems_psychiatric", LocalPatientOperations.MedicalSummary.ReorderMedicalItemProblems);
    }

    private RenderSortableGynecologicalHistory(): JSX.Element {
        // initial filter for displayable problems
        let problems = this.props.model.get("MedicalItemsGynecologicalHistory").toArray();
        problems = problems.filter(x => x.EMRTemplateIsVisibleInCPP == true && x.EMRTemplateIsInvalid == false);

        return this.RenderSortableMedicalProblem(problems, "problems_gynecological", LocalPatientOperations.MedicalSummary.ReorderMedicalItemProblems);
    }

    private RenderSortableAlertsAndSpecialNeeds(): JSX.Element {
        let alerts = PatientMedicalSummaryHelper.FilterItemsToDisplayInCPP(this.props.model.get("MedicalItemsAlertAndSpecialNeed")) as MedicalItemAlertsAndSpecialNeeds[];

        // ensure alerts with past end dates do not display in CPP
        alerts = alerts.filter(x => {
            const isValid = (x.EndDate == null) || (x.EndDate == undefined) || (x.EndDate && moment(x.EndDate) > moment());
            return isValid;
        });

        if (!alerts.any())
            return null;

        let sortableAlerts: SortableItemUIModel<MedicalItemAlertsAndSpecialNeeds>[] = [];

        // build sortableitems and sort them
        alerts.forEach(x => {
            let sortable = new SortableItemUIModel<MedicalItemAlertsAndSpecialNeeds>();
            sortable.ItemId = x.Id;
            sortable.ItemIndex = (x.EMRTemplateSortIndex == null) ? 0 : x.EMRTemplateSortIndex;
            sortable.Model = x;
            sortableAlerts.push(sortable);
        });
        sortableAlerts = ArrayHelper.SortDynamic(sortableAlerts, "ItemIndex", false);

        const self = this;
        // define the rendering function for an alert & allergy medical item
        const sortableAlertRenderFunction: ISortableItemRenderFunction<MedicalItemAlertsAndSpecialNeeds> =
            function(medicalAlert: MedicalItemAlertsAndSpecialNeeds): JSX.Element {
                return (
                    <div className="med-alert-wrapper">
                        <div className="med-alert-shrt" title={medicalAlert.Name} key={"alert_" + medicalAlert.Id} onClick={self.props.onMedicalItemClick.bind(self, medicalAlert, MedicalItemsTab.Alerts)}>
                            {medicalAlert.Name}
                        </div>

                        {cacheStore.ClinicSettings.EnableAlertsAndSpecialNeedsSettings && (
                            <div className="med-alert-tag-wrapper">
                                {!medicalAlert.EMRTemplateIsVisibleOnExport && (
                                    <Tag
                                        iconPosition="left"
                                        label={cacheStore.GetString("HiddenDLabel")}
                                        size="sm"
                                        theme="light"
                                        variant="neutral"
                                    >
                                        <Printer />
                                    </Tag>
                                )}
                            </div>
                        )}
                    </div>
                );
            };
        const sortableAlertsContainer = (
            <SortableContainerForAlerts
                ComponentId="alertsContainer"
                DragGroupClass="special-needs"
                ItemRenderingFunction={sortableAlertRenderFunction}
                SortableItems={Immutable.List(sortableAlerts)}
                OnSortDoneFunction={(itemId: string, itemIndex: number, startGroup: string, endGroup: string) =>
                    this.props.OnSortDoneFunction(LocalPatientOperations.MedicalSummary.ReorderMedicalItemAlertsSpecialNeeds, itemId, itemIndex, startGroup, endGroup)
                }
                DragStartDelegate={this.dragStartForSingleDropZone}
                DragStopDelegate={this.dragStopForSingleDropZone}
            />
        );

        return sortableAlertsContainer;
    }

    private renderSummaryComponent(item: SummaryLayoutItem): JSX.Element {
        const enableSummaryConfig = cacheStore.ClinicSettings.EnableSummaryConfiguration;
        const sectionLabel = MedicalSummaryConfigurationHelper.getLabelBySection(item.Section);
        let view: JSX.Element = null;

        if (!item.IsHidden) {
            switch (item.Section) {
                case MedicalSummarySections.PreventiveActivitiesSection:
                    if (cacheStore.ClinicSettings.EnablePreventiveCareDashboard) {
                        view = (
                            <>
                                {this.renderSectionBanner(
                                    this.props.model.get("MedicalItemsScreeningTest").toArray(),
                                    MedicalItemsTab.ScreeningTest,
                                    sectionLabel,
                                    { noteType: LocalPatientOperations.MedicalNote.Edit_NewScreeningTestItem },
                                    null,
                                    "add-new-preventive-care-item"
                                )}
                                <div>
                                    {this.renderSortableScreeningTests()}
                                </div>
                            </>
                        );
                    }
                    break;

                case MedicalSummarySections.FollowUpsSection:
                    if (cacheStore.ClinicSettings.EnableFollowUps) {
                        view = (
                            <>
                                <div className="banner">
                                    <label>{cacheStore.GetString(sectionLabel)}</label>
                                </div>
                                <div>
                                    {this.renderSortableFollowUps()}
                                </div>
                            </>
                        );
                    }
                    break;

                case MedicalSummarySections.ActiveProblemsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsProblemsOnGoing").toArray(),
                                MedicalItemsTab.MedicalProblem,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem, medicalProblemSubType: MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing }
                            )}
                            <div className={styles.activeProblemsSection}>
                                {this.RenderSortableOngoingProblemsAndConditions()}
                            </div>
                        </>
                    );
                    break;

                case MedicalSummarySections.MedicalAntecedentsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsProblemsHistory").toArray(),
                                MedicalItemsTab.MedicalProblem,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem, medicalProblemSubType: MedicalItemSubtypeEnum.ProblemAndCondition_Past }
                            )}
                            <div className={styles.activeProblemsSection}>
                                {this.RenderSortablePastProblemsAndConditions()}
                            </div>
                        </>
                    );
                    break;

                case MedicalSummarySections.ChirurgicalAntecedentsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsSurgeryHistory").toArray(),
                                MedicalItemsTab.MedicalProblem,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem, medicalProblemSubType: MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast }
                            )}
                            <div className={styles.activeProblemsSection}>
                                {this.RenderSortableSurgicalHistory()}
                            </div>
                        </>
                    );
                    break;

                case MedicalSummarySections.PsychiatricAntecedentsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsPsychiatricHistory").toArray(),
                                MedicalItemsTab.MedicalProblem,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem, medicalProblemSubType: MedicalItemSubtypeEnum.ProblemAndCondition_Psychiatric }
                            )}
                            <div className={styles.activeProblemsSection}>
                                {this.RenderSortablePsychiatricHistory()}
                            </div>
                        </>
                    );
                    break;

                case MedicalSummarySections.ObstetricalAntecedentsSestion:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsGynecologicalHistory").toArray(),
                                MedicalItemsTab.MedicalProblem,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem, medicalProblemSubType: MedicalItemSubtypeEnum.ProblemAndCondition_Gynecology },
                                <ObstetricalSummaryComponent
                                    onBannerClick={() => this.mutateState(s => s.showObstetricalSummaryPopup = true)}
                                    obstetricalSummary={this.props.model.get("ObstetricalSummary")}
                                />
                            )}
                            <div className={styles.activeProblemsSection}>
                                {this.RenderSortableGynecologicalHistory()}
                            </div>
                        </>
                    );
                    break;

                case MedicalSummarySections.ActiveMedicationSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsMedicationAndPrescription").toArray(),
                                MedicalItemsTab.Medication,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewActiveMedication }
                            )}
                            {this.RenderSortableActiveMedication()}
                        </>
                    );
                    break;

                case MedicalSummarySections.AllergiesAndIntoleranceSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsMedicalReaction").toArray(),
                                MedicalItemsTab.MedicalReactions,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewMedicalReactionItem }
                            )}
                            {this.RenderMedicalReactions()}
                        </>
                    );
                    break;

                case MedicalSummarySections.SocialHabitsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsRiskFactor").toArray(),
                                MedicalItemsTab.RiskFactors,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewRiskFactorItem }
                            )}
                            {this.RenderSortableRiskFactors()}
                        </>
                    );
                    break;

                case MedicalSummarySections.AlertsAndSpecialNeedsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsAlertAndSpecialNeed").toArray(),
                                MedicalItemsTab.Alerts,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewAlertSpecialNeedItem }
                            )}
                            {this.RenderSortableAlertsAndSpecialNeeds()}
                        </>
                    );
                    break;

                case MedicalSummarySections.ImmunizationsSection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsImmunization").toArray(),
                                MedicalItemsTab.Immunization,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewImmunizationItem }
                            )}
                            {this.RenderSortableImmunizations()}
                        </>
                    );
                    break;

                case MedicalSummarySections.FamilyHistorySection:
                    view = (
                        <>
                            {this.renderSectionBanner(
                                this.props.model.get("MedicalItemsFamilyHistory").toArray(),
                                MedicalItemsTab.FamilyHistory,
                                sectionLabel,
                                { noteType: LocalPatientOperations.MedicalNote.Edit_NewFamilyHistoryItem }
                            )}
                            {this.RenderFamilyHistory()}
                        </>
                    );
                    break;
            }
        }
        return (
            <div key={item.Section} className={styles.medicalSummaryContainer}>
                {view && (
                    <div
                        className={`summary-container drag-container ${!enableSummaryConfig ? styles.resetMinHeight : ""}`}
                        data-cy={`medicalSummary-${cacheStore.GetString(sectionLabel).replace(/\s+/g, "")}`}
                    >
                        {view}
                    </div>
                )}
            </div>
        );
    }

    private RenderSortableRiskFactors(): JSX.Element {
        const riskFactors = PatientMedicalSummaryHelper.FilterItemsToDisplayInCPP(this.props.model.get("MedicalItemsRiskFactor")) as MedicalItemRiskFactor[];
        if (!riskFactors.any())
            return null;

        let sortableRiskFactors: SortableItemUIModel<MedicalItemRiskFactor>[] = [];

        // build sortableitems and sort them
        riskFactors.forEach(x => {
            const sortable = new SortableItemUIModel<MedicalItemRiskFactor>();
            sortable.ItemId = x.Id;
            sortable.ItemIndex = (x.EMRTemplateSortIndex == null) ? 0 : x.EMRTemplateSortIndex;
            sortable.Model = x;
            sortableRiskFactors.push(sortable);
        });
        sortableRiskFactors = ArrayHelper.SortDynamic(sortableRiskFactors, "ItemIndex", false);

        const self = this;

        // define the rendering function for an alert & allergy medical item
        const sortableRiskFactorRenderFunction: ISortableItemRenderFunction<MedicalItemRiskFactor> =
            function(riskFactor: MedicalItemRiskFactor): JSX.Element {
                return <div className="med-act-wrp" key={"rf_" + riskFactor.Id} onClick={self.props.onMedicalItemClick.bind(self, riskFactor, MedicalItemsTab.RiskFactors)}>{MedicalItemRiskFactorHelper.getRiskFactorLabel(riskFactor, true)}</div>;
            };

        const sortableAlertsContainer = (
            <SortableContainerForRiskFactors
                ComponentId="riskFactorContainer"
                DragGroupClass="social-habits"
                ItemRenderingFunction={sortableRiskFactorRenderFunction}
                SortableItems={Immutable.List(sortableRiskFactors)}
                OnSortDoneFunction={(itemId: string, itemIndex: number, startGroup: string, endGroup: string) =>
                    this.props.OnSortDoneFunction(LocalPatientOperations.MedicalSummary.ReorderMedicalItemSocialHabits, itemId, itemIndex, startGroup, endGroup)
                }
                DragStartDelegate={this.dragStartForSingleDropZone}
                DragStopDelegate={this.dragStopForSingleDropZone}
            />
        );

        return sortableAlertsContainer;
    }

    private RenderSortableImmunizations(): JSX.Element {
        const immunizations = PatientMedicalSummaryHelper.FilterItemsToDisplayInCPP(this.props.model.get("MedicalItemsImmunization")) as MedicalItemImmunization[];
        if (!immunizations.any())
            return null;

        let sortableImmunizations: SortableItemUIModel<MedicalItemImmunization>[] = [];

        // build sortableitems and sort them
        immunizations.forEach(x => {
            const sortable = new SortableItemUIModel<MedicalItemImmunization>();
            sortable.ItemId = x.Id;
            sortable.ItemIndex = (x.EMRTemplateSortIndex == null) ? 0 : x.EMRTemplateSortIndex;
            sortable.Model = x;
            sortableImmunizations.push(sortable);
        });
        sortableImmunizations = ArrayHelper.SortDynamic(sortableImmunizations, "ItemIndex", false);

        const self = this;

        // define the rendering function for an alert & allergy medical item
        const sortablImmunizationRenderFunction: ISortableItemRenderFunction<MedicalItemImmunization> =
            function(i: MedicalItemImmunization): JSX.Element {
                const invalidClass = i.IsRefused ? " red-text" : "";
                const displayName = QuebecVaccinationRegistryHelper.getImmunizationDisplayName(i);

                return (
                    <div className={"med-act-wrp clickable" + invalidClass} key={"alert_" + i.Id} onClick={self.props.onMedicalItemClick.bind(self, i, MedicalItemsTab.Immunization)}>
                        {displayName}
                        {i.QuebecRegistryRefusalMotiveCode ? ` - ${cacheStore.GetString("MedicalSummaryRefusedLabel")}` : ""}
                        {(i.IsRefused
                            ? i.RefusedDate &&
                            ` (${PatientMedicalItemsHelper.GetLifeStageDateString(i.RefusedDate, i.RefusedPrecisionDate)})`
                            : i.AdministeredDate && (
                                <span className="text-gray"> ({PatientMedicalItemsHelper.GetLifeStageDateString(i.AdministeredDate, i.AdministeredPrecisionDate)})</span>
                            )
                        )
                        }
                    </div>
                );
            };

        const sortableImmunizationComponent = (
            <SortableContainerForImmunizations
                ComponentId="immunizationsContainer"
                DragGroupClass="immunizations"
                ItemRenderingFunction={sortablImmunizationRenderFunction}
                SortableItems={Immutable.List(sortableImmunizations)}
                OnSortDoneFunction={(itemId: string, itemIndex: number, startGroup: string, endGroup: string) =>
                    this.props.OnSortDoneFunction(LocalPatientOperations.MedicalSummary.ReorderMedicalItemImmunizations, itemId, itemIndex, startGroup, endGroup)
                }
                DragStartDelegate={this.dragStartForSingleDropZone}
                DragStopDelegate={this.dragStopForSingleDropZone}
            />
        );

        return sortableImmunizationComponent;
    }

    private RenderSortableActiveMedication(): JSX.Element {
        const medications = this.props.model
            .get("MedicalItemsMedicationAndPrescription")
            .filter(medication => medication.Model.SummaryDisplayStatus != EnumPatientSummaryMedicationDisplayStatus.Hide)
            .toArray();

        if (!medications.any())
            return null;

        let sortableMedications: SortableItemUIModel<MedicationModel>[] = [];

        // build sortableitems and sort them
        medications.forEach(x => {
            const sortable = new SortableItemUIModel<MedicationModel>();
            sortable.ItemId = x.Model.Id;
            sortable.ItemIndex = (x.Model.EMRTemplateSortIndex == null) ? 0 : x.Model.EMRTemplateSortIndex;
            sortable.Model = x;
            sortableMedications.push(sortable);
        });
        sortableMedications = ArrayHelper.SortDynamic(sortableMedications, "ItemIndex", false);

        // define the rendering function
        const sortableMedicationRenderFunction: ISortableItemRenderFunction<MedicationModel> =
            (i: MedicationModel): JSX.Element => {
                const refillString = i.GetRenewalString();
                const expiredClassName = i.Model.SummaryDisplayStatus == 1 ? "red-text" : "";

                return (
                    <div key={"medication_" + i.Model.Id} onClick={() => this.props.onActiveMedicationDetailsClick()}>
                        <div className="med-act-wrp">
                            <span className={styles.medicationNameContainer}>
                                {i.isLongTerm() && (<LongTermMedicationIcon />)}
                                {i.GetName()}
                            </span>
                            <span className={i.isExpiredLongTermMed() ? "red-text" : "text-gray"}>  {i.GetDosageString({ includeNarcoticFreq: false })}</span>
                            <strong className={expiredClassName}> {refillString}</strong>
                        </div>
                    </div>
                );
            };

        return (
            <SortableContainerForActiveMedication
                ComponentId="medicationContainer"
                DragGroupClass="active-meds"
                isMedicationModel
                ItemRenderingFunction={sortableMedicationRenderFunction}
                SortableItems={Immutable.List(sortableMedications)}
                OnSortDoneFunction={(itemId: string, itemIndex: number, startGroup: string, endGroup: string) =>
                    this.props.OnSortDoneFunction(LocalPatientOperations.MedicalSummary.ReorderMedicalItemActiveMedication, itemId, itemIndex, startGroup, endGroup)
                }
                DragStartDelegate={this.dragStartForSingleDropZone}
                DragStopDelegate={this.dragStopForSingleDropZone}
            />
        );
    }

    private RenderFamilyHistory(): JSX.Element {
        let result: JSX.Element = null;

        // Relationship lookup codes.
        const relationshipLookupCodes = [
            Relationship.MaternalGrandMother,
            Relationship.MaternalGrandFather,
            Relationship.PaternalGrandMother,
            Relationship.PaternalGrandFather,
            Relationship.Father,
            Relationship.Mother,
            Relationship.Sister,
            Relationship.Brother,
            Relationship.Daughter,
            Relationship.Son,
            Relationship.Cousin,
            Relationship.MaternalSibling,
            Relationship.PaternalSibling
        ];

        // get relationship lookups for filtering
        const relationshipLookups = {};

        for (const code of relationshipLookupCodes)
            relationshipLookups[code] = cacheStore.FindLookupByLookupCode(code, Relationship.Code);

        const familyHistoryByRelationshipId =
            (PatientMedicalSummaryHelper.FilterItemsToDisplayInCPP(this.props.model.get("MedicalItemsFamilyHistory")) as MedicalItemFamilyHistory[])
                .groupBy(entry => entry.RelationshipLookupId);

        if (!familyHistoryByRelationshipId.any())
            return null;

        // filter history
        const familyHistory = {};
        let otherHistory: { groupName: string, medicalItemFamilyHistory: MedicalItemFamilyHistory[] }[] = [];
        let noTypeHistory = [];

        for (let group of familyHistoryByRelationshipId) {
            const lookup = cacheStore.GetLookupById(group[0].RelationshipLookupId);

            if (relationshipLookupCodes.contains(lookup?.LookupCode)) {
                familyHistory[lookup.LookupCode] = group;
            }
            else if (lookup && lookup.LookupCode == Relationship.Other) {
                otherHistory = group
                    .groupBy(medicalItem => medicalItem.SpecifiedRelationship)
                    .map(value => ({
                        groupName: value[0].SpecifiedRelationship,
                        medicalItemFamilyHistory: value
                    }));
            }
            else {
                noTypeHistory = [...noTypeHistory, group];
            }
        }

        for (const code of relationshipLookupCodes)
            familyHistory[code] = familyHistory[code] ?? [];

        const self = this;

        // function for building family history string
        const familyHistoryStringBuilder: (inputArray: MedicalItemFamilyHistory[]) => JSX.Element[] =
            function(inputArray: MedicalItemFamilyHistory[]): JSX.Element[] {
                const result: JSX.Element[] = [];

                if (inputArray && inputArray.length > 0) {
                    inputArray.forEach((x, idx) => {
                        let str = "";

                        const comma = (idx == inputArray.length - 1) ? "" : ",";
                        str = x.Problem;

                        // get age lifestage
                        if (x.LifeStageMode && x.LifeStageMode == 1) {
                            const age = x.LifeStageAge;
                            if (age && age > 0) {
                                str += " (" + age + " " + cacheStore.GetLocalizedValueByLookupId(x.LifeStageAgeStatusLookupId) + ")";
                            }
                        }
                        else if (x.LifeStageStatusLookupId && x.LifeStageMode == 2) {
                            const ageStage = cacheStore.GetLocalizedValueByLookupId(x.LifeStageStatusLookupId);
                            str += " ( " + ageStage + " )";
                        }

                        result.push(<span key={"fh_" + x.MedicalItemId} className="clickable" onClick={self.props.onMedicalItemClick.bind(self, x, MedicalItemsTab.FamilyHistory)}> {str}{comma} </span>);
                    });
                }

                return result;
            };

        const familyHistoryStrings = {};
        for (const code of Object.keys(familyHistory))
            familyHistoryStrings[code] = familyHistoryStringBuilder(familyHistory[code]);

        const otherHistoryString: JSX.Element[] = [];
        otherHistory.forEach(other => {
            const history = (
                <div key={"oth" + other.medicalItemFamilyHistory[0].MedicalItemId} className="flex-container">
                    <div className="family-hst a">
                        <strong>{cacheStore.GetString("OtherLabel").toUpperCase()} ({other.groupName}): </strong>
                    </div>
                    <div className="family-hst b">
                        {familyHistoryStringBuilder(other.medicalItemFamilyHistory)}
                    </div>
                </div>
            );
            otherHistoryString.push(history);
        });

        const emptyTypeHistoryString: JSX.Element[] = [];
        noTypeHistory.forEach(noTypeHist => {
            const history = (
                <div key={"oth" + noTypeHist.MedicalItemId} className="flex-container">
                    <div className="family-hst a">
                        <strong>{noTypeHist.Problem}</strong>
                    </div>
                    <div className="family-hst b" />
                </div>
            );
            emptyTypeHistoryString.push(history);
        });

        const relationshipNoSiblingsLookupCodes = relationshipLookupCodes
            .filter(c => c != Relationship.Sister && c != Relationship.Brother);

        result = (
            <div>
                {relationshipNoSiblingsLookupCodes.map(code => {
                    if (familyHistoryStrings[code].length > 0) {
                        return (
                            <div key={"fam" + familyHistory[code][0].MedicalItemId} className="flex-container">
                                <div className="family-hst a">
                                    <strong>{cacheStore.GetLocalizedValueByLookupId(relationshipLookups[code].LookupID).toUpperCase()}: </strong>
                                </div>
                                <div className="family-hst b">
                                    {familyHistoryStrings[code]}
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
                {((familyHistoryStrings[Relationship.Sister].length > 0) || (familyHistoryStrings[Relationship.Brother].length > 0)) && (
                    <div className="flex-container">
                        <div className="family-hst a">
                            <strong>{cacheStore.GetString("labelSiblings").toUpperCase()}: </strong>
                        </div>
                        <div className="family-hst b" />
                    </div>
                )}
                {familyHistoryStrings[Relationship.Sister].length > 0 && (
                    <div className="flex-container">
                        <div className="family-hst a">
                            <span className="myle-icon-16 female" />
                        </div>
                        <div className="family-hst b">
                            {familyHistoryStrings[Relationship.Sister]}
                        </div>
                    </div>
                )}
                {familyHistoryStrings[Relationship.Brother].length > 0 && (
                    <div className="flex-container">
                        <div className="family-hst a">
                            <span className="myle-icon-16 male" />
                        </div>
                        <div className="family-hst b">
                            {familyHistoryStrings[Relationship.Brother]}
                        </div>
                    </div>
                )}
                {noTypeHistory.length > 0 &&
                    emptyTypeHistoryString
                }
                {otherHistoryString.length > 0 &&
                    otherHistoryString
                }
            </div>
        );

        return result;
    }

    private RenderMedicalReactions(): JSX.Element {
        let result: JSX.Element = null;

        const self = this;

        // get reaction type looks for filtering
        const allergyTypeLookup = cacheStore.FindLookupByLookupCode("Allergy ");
        const adverseReactionTypeLookup = cacheStore.FindLookupByLookupCode("Adverse Reaction ");
        const intoleranceTypeLookup = cacheStore.FindLookupByLookupCode("Intolerance");

        // filter reaction types in allergies/reactions/intolerances
        const medicalReactions = PatientMedicalSummaryHelper.FilterItemsToDisplayInCPP(this.props.model.get("MedicalItemsMedicalReaction")) as MedicalItemMedicalReaction[];
        if (!medicalReactions.any())
            return null;

        const allergies = medicalReactions.filter(x => x.ReactionTypeLookupId == allergyTypeLookup.LookupID);
        const reactions = medicalReactions.filter(x => x.ReactionTypeLookupId == adverseReactionTypeLookup.LookupID);
        const intolerances = medicalReactions.filter(x => x.ReactionTypeLookupId == intoleranceTypeLookup.LookupID);

        // function for building medical reactions display strings
        const medicalReactionStringPrinter: (inputArray: MedicalItemMedicalReaction[]) => JSX.Element[] =
            function(inputArray: MedicalItemMedicalReaction[]): JSX.Element[] {
                const result: JSX.Element[] = [];

                const nothing = <span key="mr_nothing" className="clickable" onClick={self.props.onMedicalItemClick.bind(self, null, MedicalItemsTab.MedicalReactions)}>--</span>;

                if (inputArray && inputArray.length > 0) {
                    inputArray.forEach((x, idx) => {
                        const val = x.OffendingAgent.toUpperCase();
                        const comma = (idx == inputArray.length - 1) ? "" : ";";

                        if (idx <= inputArray.length - 1)
                            result.push(<span key={"mr_" + x.MedicalItemId} className="clickable" onClick={self.props.onMedicalItemClick.bind(self, x, MedicalItemsTab.MedicalReactions)}>  {val}{comma} </span>);
                    });
                }
                else {
                    result.push(nothing);
                }

                return result;
            };

        // medical reactions strings to display
        const allergiesString = medicalReactionStringPrinter(allergies);
        const reactionsString = medicalReactionStringPrinter(reactions);
        const intoleranceString = medicalReactionStringPrinter(intolerances);

        result = (
            <div>
                <div className="flex-container">
                    <div className="medical-reaction c1">
                        <ul className="circles">
                            <li>
                                <span className="circle-red">
                                    <i>A</i>
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className="medical-reaction c2">
                        {allergiesString}
                    </div>
                </div>

                <div className="flex-container">
                    <div className="medical-reaction c1">
                        <ul className="circles">
                            <li>
                                <span className="circle-grayblue">
                                    <i>I</i>
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className="medical-reaction c2">
                        {intoleranceString}
                    </div>
                </div>
                <div className="flex-container">
                    <div className="medical-reaction c1">
                        <ul className="circles">
                            <li>
                                <span className="circle-gray">
                                    <i>R</i>
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className="medical-reaction c2">
                        {reactionsString}
                    </div>
                </div>
            </div>
        );

        return result;
    }

    private RenderVitalSigns(): JSX.Element {
        return (
            <VitalSignsView
                showPlus={cacheStore.ClinicSettings.EnableDirectPatientProfileEdits && !this.props.isReadOnly}
                currentPatient={this.props.currentPatient}
                vitalSign={this.state.latestVitalSign}
                onVitalSignClick={() => this.props.onMedicalItemClick(this.state.latestVitalSign, MedicalItemsTab.VitalSigns)}
                onArrowClick={() => this.props.onSwitchView(this.state.latestVitalSign, MedicalItemsTab.VitalSigns)}
                onPlusClick={() => this.onPlusClick(LocalPatientOperations.MedicalNote.Edit_NewVitalSignItem)}
            />
        );
    }

    private renderCommentsBody(comments: Comment[]): JSX.Element[] {
        return (
            comments.map((comment: Comment, idx: number) => {
                const employeeAsPerson = cacheStore.GetCliniciansPersonsByContactFacetId(comment.EmployeeId);
                const commentBy = employeeAsPerson ? employeeAsPerson.NameWithPrefix : "";
                const noBorderClass = (idx === comments.length - 1) ? " border-none" : "";

                return (
                    <div
                        key={`Comment_${comment.Id}_${idx}`}
                        className={"notes-collection-item" + noBorderClass}
                    >
                        <div className="memo-header">
                            <strong>{`${commentBy} `}</strong>
                            <span>{this.getCommentTitle(comment)}</span>
                        </div>
                        <div className="note-container">
                            {comment.Value}
                        </div>
                    </div>
                );
            })
        );
    }

    private getCommentTitle(comment: Comment): string {
        if (!comment.CreateDate) {
            return "";
        }

        const dateFormat = "YYYY-MM-DD";
        return `${cacheStore.GetString("OnLabel").toLocaleLowerCase()}
                                ${moment(comment.CreateDate).format(dateFormat)}
                                ${cacheStore.GetString("AtLabel")}
                                ${moment(comment.CreateDate).format("HH:mm:ss")}`;
    }

    private onFollowUpItemClick(item: FollowUpItem): void {
        const itemId = item.model.Id;
        const encounterId = item.model.EncounterId;

        const encounter = this.props.model
            .get("Encounters")
            .toArray()
            .firstOrDefault(p => p.Id == encounterId);

        if (cacheStore.ClinicSettings.EnableMedicalNoteProtection && !encounter) {
            if (item.getFollowUpType() == MedicalRequestType.LabRequest)
                sessionStore.showPopup(cacheStore.GetString("RoleBasedAccessControl_ExamRequestIsProtected"));
            else
                sessionStore.showPopup(cacheStore.GetString("RoleBasedAccessControl_ConsultationRequestIsProtected"));
        }
        else {
            this.mutateState(s => {
                s.showLabRequestPopup = item.getFollowUpType() == MedicalRequestType.LabRequest;
                s.showConsultationPopup = !s.showLabRequestPopup;
                s.selectedFollowUpId = itemId;
                s.selectedEncounterId = encounterId;
            });
        }
    }

    private closeRequestPopup(): void {
        this.mutateState(s => {
            s.showLabRequestPopup = false;
            s.showConsultationPopup = false;
            s.selectedFollowUpId = null;
            s.selectedEncounterId = null;
        });
    }

    private dragStartForMultipleDropZones(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-scope-a").find(".drag-container").addClass("over");
    }

    private dragStopForMultipleDropZones(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-scope-a").find(".drag-container").removeClass("over");
    }

    private dragStartForSingleDropZone(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-container").addClass("over");
    }

    private dragStopForSingleDropZone(event: JQueryEventObject, ui: JQueryUI.SortableUIParams): void {
        ui.item.closest(".drag-container").removeClass("over");
    }

    private getScreeningsTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const dateFormat = "YYYY/MM/DD";

        return MyleTableColumn.buildList<ScreeningTestTableItemModel>(columnsOptions, column => {
            switch (column.FieldName) {
                case "Item":
                    return {
                        renderCell: item => {
                            const testName = item.Model.getScreeningTestTitle();
                            return (<div><SpanControl title={testName} text={testName} /></div>);
                        }
                    };

                case "DueDate":
                    return {
                        renderCell: item => {
                            let medicalItem = item.Model.getMedicalItem();
                            let dueDate: string = medicalItem.DueDate ? moment(medicalItem.DueDate).format(dateFormat) : "";
                            return (<div><SpanControl text={dueDate} /></div>);
                        }
                    };

                case "Frequency":
                    return {
                        renderCell: item => (
                            <div><SpanControl text={item.Model.getFrequency()?.getNameAbbr?.()} /></div>
                        )
                    };

                case "Status":
                    return {
                        renderCell: item => (
                            <div>
                                <SpanControl title={item.Model.getCPPDisplayStatus()} text={item.Model.getCPPDisplayStatus()} />
                                <ScreeningTestWarningIcon screeningTest={item.Model} />
                            </div>
                        )
                    };

                case "LastCompletionDate":
                    return {
                        renderCell: item => {
                            let completedScreeningTests = this.props.model.get("MedicalItemsScreeningTest").toArray()
                                .filter(x => x.EMRTemplateId == item.Model.model.EMRTemplateId && x.CompletionDate != null);
                            let lastCompletedItem = completedScreeningTests.sortByPropertyDesc(x => x.CompletionDate).firstOrDefault();

                            return (<div><SpanControl text={lastCompletedItem ? moment(lastCompletedItem.CompletionDate.toString()).format(dateFormat) : ""} /></div>);
                        }
                    };

                case "Notes":
                    return {
                        renderCell: item => {
                            const medicalItem = item.Model.getMedicalItem();
                            const notes: string = medicalItem.Notes ? medicalItem.Notes : "";
                            const interpretation: string = new MedicalItemScreeningTest(medicalItem).isInterpreted()
                                ? `${cacheStore.GetLocalizedValueByLookupId(medicalItem.InterpretationLookupId)} (${moment(medicalItem.InterpretationDate.toString()).format(dateFormat)}) `
                                : "";

                            return (
                                <div title={interpretation + notes}>
                                    <SpanControl IsBold={true} text={interpretation} />
                                    <SpanControl text={notes} />
                                </div>
                            );
                        }
                    };

                case "Comments":
                    const imgClass: string = "no-hover paperClip-rotation";
                    const paperClipImg: JSX.Element = <img src={img_icon_attachment_icon} className={imgClass} alt={cacheStore.GetString("AttachmentLabel")} />;

                    return {
                        renderHeader: () => (
                            <div>
                                <a className="paperClip-table-header">{paperClipImg}</a>
                                <a>{" / " + cacheStore.GetString("CommentsLabel")}</a>
                            </div>
                        ),
                        renderCell: item => this.renderAttachmentsAndCommentsCell(item, paperClipImg)
                    };
            }
        });
    }

    private getFollowUpTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const dateFormat = "YYYY/MM/DD";

        return MyleTableColumn.buildList<FollowUpTableItemModel>(columnsOptions, column => {
            switch (column.FieldName) {
                case "Item":
                    return {
                        renderCell: item => {
                            const followUpItem = new FollowUpItem(item.Model.model);
                            const followUpName = followUpItem.getSpeciality();

                            return (<div><SpanControl title={followUpName} text={followUpName} /></div>);
                        }
                    };

                case "Reason":
                    return {
                        renderCell: item => {
                            const reason: string = item.Model.model.Reason ? item.Model.model.Reason : "";

                            return (<div><SpanControl title={reason} text={reason} /></div>);
                        }
                    };

                case "CreationDate":
                    return {
                        renderCell: item => {
                            const creationDate: string = item.Model.model.CreationDate ? moment(item.Model.model.CreationDate).format(dateFormat) : "";

                            return (<div><SpanControl text={creationDate} /></div>);
                        }
                    };

                case "DueDate":
                    return {
                        renderCell: item => {
                            const dueDate: string = item.Model.model.DueDate ? moment(item.Model.model.DueDate).format(dateFormat) : "";

                            return (<div><SpanControl text={dueDate} /></div>);
                        }
                    };

                case "Priority":
                    return {
                        renderCell: item => {
                            const followUpItem = new FollowUpItem(item.Model.model);
                            const priorityName = followUpItem.getPriority().getName();
                            const urgentStyle: React.CSSProperties = followUpItem.isUrgent() ? { color: "red" } : {};

                            return (<div><SpanControl style={urgentStyle} text={priorityName} /></div>);
                        }
                    };

                case "Status":
                    return {
                        renderCell: item => {
                            const statusChangeDate = item.Model.model.StatusChangeDate
                                ? moment(item.Model.model.StatusChangeDate).format(dateFormat)
                                : moment(item.Model.model.CreationDate).format(dateFormat);

                            const statusAndDateChange = `${cacheStore.GetLocalizedValueByLookupId(item.Model.model.StatusLookupId)} (${statusChangeDate})`;

                            return (<div><SpanControl title={statusAndDateChange} text={statusAndDateChange} /></div>);
                        }
                    };

                case "LatestComment":
                    return {
                        renderCell: item => (
                            <div><SpanControl title={item.Model.model.LatestComment} text={item.Model.model.LatestComment} /></div>
                        )
                    };
                    break;
            }
        });
    }

    private renderAttachmentsAndCommentsCell(item: ScreeningTestTableItemModel, paperClipImg: JSX.Element): JSX.Element {
        const medicalItem = item.Model.getMedicalItem();
        const emrTemplateId = medicalItem.EMRTemplateId;
        let relatedEmrTemplateComments: Comment[] = [];

        if (this.props.model.get("EMRTemplateComments") && this.props.model.get("EMRTemplateComments")[emrTemplateId])
            relatedEmrTemplateComments = this.props.model.get("EMRTemplateComments")[emrTemplateId];

        let notesCountRenderClass = "no-hover ";
        if (relatedEmrTemplateComments.length > 0)
            notesCountRenderClass = "screening-comment-and-attachments pointer";

        const notesCountRender = (
            <a
                onClick={(event: React.MouseEvent) => this.mutateState(s => {
                    s.selectedScreeningTest = item.Model;
                    s.commentsPopupClientX = event.clientX;
                    s.commentsPopupClientY = event.clientY;
                    s.showCommentPopup = true;
                })}
                className={notesCountRenderClass}
                key="screeningNotes"
            >
                [<span id={emrTemplateId} className="red"> {relatedEmrTemplateComments.length} </span>]
            </a>
        );

        return (
            <div className="attachments-notes-container">
                <span onClick={e => this.viewScreeningAttachments(item.Model.getMedicalItem().Attachments, e)}>
                    {medicalItem.Attachments && medicalItem.Attachments.length > 0 &&
                        paperClipImg
                    }
                </span>
                <span>
                    {relatedEmrTemplateComments.length > 0 &&
                        notesCountRender
                    }
                </span>
            </div>
        );
    }

    private getScreeningsMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const cols: MyleTableColumnOptions[] = [];

        const itemColumn = new MyleTableColumnOptions();
        itemColumn.HeaderText = cacheStore.GetString("ItemLabel");
        itemColumn.FieldName = "Item";
        itemColumn.IsFixedInPlace = true;
        itemColumn.RowClickEnabled = true;
        itemColumn.IsSortable = false;
        itemColumn.WidthPercentageDefault = 20;
        itemColumn.IsStaticWidth = true;

        const dueDateColumn = new MyleTableColumnOptions();
        dueDateColumn.HeaderText = cacheStore.GetString("DueDateLabel");
        dueDateColumn.FieldName = "DueDate";
        dueDateColumn.RowClickEnabled = true;
        dueDateColumn.IsSortable = false;
        dueDateColumn.WidthPercentageDefault = 10;
        dueDateColumn.IsStaticWidth = true;

        const frequencyColumn = new MyleTableColumnOptions();
        frequencyColumn.HeaderText = cacheStore.GetString("FrequencyLabel");
        frequencyColumn.FieldName = "Frequency";
        frequencyColumn.RowClickEnabled = true;
        frequencyColumn.IsSortable = false;
        frequencyColumn.WidthPercentageDefault = 10;
        frequencyColumn.IsStaticWidth = true;

        const statusColumn = new MyleTableColumnOptions();
        statusColumn.HeaderText = cacheStore.GetString("StatusLabel");
        statusColumn.FieldName = "Status";
        statusColumn.RowClickEnabled = true;
        statusColumn.IsSortable = false;
        statusColumn.WidthPercentageDefault = 10;
        statusColumn.IsStaticWidth = true;

        const lastCompletionColumn = new MyleTableColumnOptions();
        lastCompletionColumn.HeaderText = cacheStore.GetString("LastCompletionLabel");
        lastCompletionColumn.FieldName = "LastCompletionDate";
        lastCompletionColumn.RowClickEnabled = true;
        lastCompletionColumn.IsSortable = false;
        lastCompletionColumn.WidthPercentageDefault = 10;
        lastCompletionColumn.IsStaticWidth = true;

        const notesColumn = new MyleTableColumnOptions();
        notesColumn.HeaderText = cacheStore.GetString("NotesLabel");
        notesColumn.FieldName = "Notes";
        notesColumn.RowClickEnabled = true;
        notesColumn.IsSortable = false;
        notesColumn.WidthPercentageDefault = 30;
        notesColumn.IsStaticWidth = true;

        const commentsColumn = new MyleTableColumnOptions();
        commentsColumn.HeaderText = "";
        commentsColumn.FieldName = "Comments";
        commentsColumn.RowClickEnabled = false;
        commentsColumn.IsSortable = false;
        commentsColumn.WidthPercentageDefault = 10;
        commentsColumn.IsStaticWidth = true;

        cols.push(itemColumn);
        cols.push(dueDateColumn);
        cols.push(frequencyColumn);
        cols.push(statusColumn);
        cols.push(lastCompletionColumn);
        cols.push(notesColumn);
        cols.push(commentsColumn);

        return cols;
    }

    private getFollowUpMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const cols: MyleTableColumnOptions[] = [];

        const itemColumn = new MyleTableColumnOptions();
        itemColumn.HeaderText = cacheStore.GetString("ItemLabel");
        itemColumn.FieldName = "Item";
        itemColumn.IsFixedInPlace = true;
        itemColumn.RowClickEnabled = true;
        itemColumn.IsSortable = false;
        itemColumn.WidthPercentageDefault = 20;
        itemColumn.IsStaticWidth = true;

        const reasonColumn = new MyleTableColumnOptions();
        reasonColumn.HeaderText = cacheStore.GetString("ReasonLabel");
        reasonColumn.FieldName = "Reason";
        reasonColumn.RowClickEnabled = true;
        reasonColumn.IsSortable = false;
        reasonColumn.WidthPercentageDefault = 10;
        reasonColumn.IsStaticWidth = true;

        const priorityColumn = new MyleTableColumnOptions();
        priorityColumn.HeaderText = cacheStore.GetString("PriorityLabel");
        priorityColumn.FieldName = "Priority";
        priorityColumn.RowClickEnabled = true;
        priorityColumn.IsSortable = false;
        priorityColumn.WidthPercentageDefault = 10;
        priorityColumn.IsStaticWidth = true;

        const creationDateColumn = new MyleTableColumnOptions();
        creationDateColumn.HeaderText = cacheStore.GetString("CreationDateLabel");
        creationDateColumn.FieldName = "CreationDate";
        creationDateColumn.RowClickEnabled = true;
        creationDateColumn.IsSortable = false;
        creationDateColumn.WidthPercentageDefault = 10;
        creationDateColumn.IsStaticWidth = true;

        const dueDateColumn = new MyleTableColumnOptions();
        dueDateColumn.HeaderText = cacheStore.GetString("DueDateLabel");
        dueDateColumn.FieldName = "DueDate";
        dueDateColumn.RowClickEnabled = true;
        dueDateColumn.IsSortable = false;
        dueDateColumn.WidthPercentageDefault = 10;
        dueDateColumn.IsStaticWidth = true;

        const statusColumn = new MyleTableColumnOptions();
        statusColumn.HeaderText = cacheStore.GetString("StatusLabel");
        statusColumn.FieldName = "Status";
        statusColumn.RowClickEnabled = true;
        statusColumn.IsSortable = false;
        statusColumn.WidthPercentageDefault = 20;
        statusColumn.IsStaticWidth = true;

        const latestCommentColumn = new MyleTableColumnOptions();
        latestCommentColumn.HeaderText = cacheStore.GetString("LastCommentLabel");
        latestCommentColumn.FieldName = "LatestComment";
        latestCommentColumn.RowClickEnabled = true;
        latestCommentColumn.IsSortable = false;
        latestCommentColumn.WidthPercentageDefault = 10;
        latestCommentColumn.IsStaticWidth = true;

        cols.push(itemColumn);
        cols.push(reasonColumn);
        cols.push(priorityColumn);
        cols.push(creationDateColumn);
        cols.push(dueDateColumn);
        cols.push(statusColumn);
        cols.push(latestCommentColumn);

        return cols;
    }

    private mutateState(mutation: (s: MedicalSummaryState) => void, then: any = null): void {
        let s = new MedicalSummaryState();
        mutation(s);
        this.setState(s, then);
    }

    private updateSummaryConfiguration(): void {
        this.mutateState(s => s.summaryConfiguration = MedicalSummaryConfigurationHelper.getSpecificOrDefaultSummaryConfigurationGridLayout());
    }

    private updateNotesConfiguration(): void {
        this.mutateState(s => s.notesConfiguration = MedicalNotesConfigurationHelper.getSpecificConfigAndUpdateNotesFilterSettings());
    }

    private viewScreeningAttachments(screeningAttachments: AttachedObjectResponse[], e: React.MouseEvent): void {
        const eventX = e.clientX.toString();
        const eventY = e.clientY.toString();
        const setContainerToTheLeft = -350;

        let attachments: OpenAttachmentsPopupItem[] = screeningAttachments.map(x => {
            let tmpAtt = new OpenAttachmentsPopupItem();
            tmpAtt.fileTitle = x.Description;
            tmpAtt.openFileCallback = () => x && ExportableFileHelper.fromAttachedObject(x).openInNewWindow();

            return tmpAtt;
        });

        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.DisplayOpenAttachmentPopup,
            {
                Attachments: attachments,
                Xpos: Number(eventX) + setContainerToTheLeft,
                Ypos: Number(eventY)
            }
        );
    }

    private getFollowUpsToDisplay(): FollowUpItem[] {
        const followUps: FollowUpResponse[] = this.props.model.get("FollowUps").toArray();
        if (!followUps || followUps.length === 0)
            return [];

        return followUps.map(x => new FollowUpItem(x)).filter(x => !x.isInvalid() && x.isVisibleInCPP()).sortByProperties(propertyComparer(x => DateTime.getDateOrMax(x.model.DueDate)), propertyComparer(x => DateTime.getDateOrMax(x.model.CreationDate)));
    }

    private async onMedicalRequestPrintSuccess(fileName: ExportableFile, id: string, medicalRequestType: MedicalRequestType): Promise<void> {
        if (!fileName)
            return;

        PrintHelper.ShowPrintPreviewFileName((await fileName.getFetchUrlDtoAsync())?.FetchUrl);
        this.props.onUpdateScreeningsModelAfterPrintingRequest(id, medicalRequestType);
    }

    private onPlusClick(noteType?: LocalPatientOperations.MedicalNote, medicalProblemSubType?: MedicalItemSubtypeEnum): void {
        sessionStore.dispatch(noteType, {
            editContext: {
                selectedMedicalProblemSubType: medicalProblemSubType,
                isMedicalItemFromOutsideNote: true,
                medicalItemPreviousRoute: noteType,
                isCreatingNewMedicalItem: true
            }
        });
    }

    private renderSectionBanner(
        medicalItems: any[],
        itemType: MedicalItemsTab,
        label: string,
        localPatientOperation?: { noteType?: LocalPatientOperations.MedicalNote, medicalProblemSubType?: MedicalItemSubtypeEnum },
        summaryElement: JSX.Element = null,
        dataCy: string = null
    ): JSX.Element {
        const enableDirectPatientProfileEdits = cacheStore.ClinicSettings.EnableDirectPatientProfileEdits;
        const { noteType, medicalProblemSubType } = localPatientOperation;

        return (
            <div className={`banner ${styles.medicalItemWrapper}`}>
                {!this.props.isReadOnly && (
                    <div className="banner-btn">
                        {noteType === LocalPatientOperations.MedicalNote.Edit_NewActiveMedication && enableDirectPatientProfileEdits && (
                            <a
                                onClick={() => {
                                    sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, {
                                        value: PatientTab.Prescription,
                                        isPrescriptionTabActive: true,
                                        isMedicalItemFromOutsideNote: true
                                    });
                                }}
                                className={styles.newPrescriptionButton}
                            >
                                {cacheStore.GetString("ConfigureSummary_NewRxLabel")}
                            </a>
                        )}
                        {noteType && enableDirectPatientProfileEdits && (
                            <PlusIconButton
                                onClick={() => this.onPlusClick(noteType, medicalProblemSubType)}
                                dataCy={dataCy}
                            />
                        )}
                    </div>
                )}
                {summaryElement}
                <label
                    className={medicalItems.any() ? styles.medicalItemLabel : ""}
                    onClick={() => medicalItems.any() && this.props.onSwitchView(medicalItems[0], itemType)}
                >
                    {cacheStore.GetString(label)}
                </label>
            </div>
        );
    }

    private renderObstetricalSummaryPopup(): JSX.Element {
        return (
            <ObstetricalSummaryPopup
                obstetricalSummary={this.props.model.get("ObstetricalSummary")}
                onSave={obstetricalSummary => this.onObstetricalSummarySave(obstetricalSummary)}
                onClose={() => this.mutateState(s => s.showObstetricalSummaryPopup = false)}
            />
        );
    }

    private onObstetricalSummarySave(obstetricalSummary: ObstetricalSummary): void {
        this.props.onSaveObstetricalSummary(obstetricalSummary);
        this.mutateState(s => s.showObstetricalSummaryPopup = false);
    }

    private calculatePercentile(state: MedicalSummaryState, property: VitalProperty, vitalSign: MedicalItemVitalSign = null): void {
        if (!state.percentiles)
            state.percentiles = {};

        state.percentiles[property] = this.vitalSignPercentileUtility?.calculatePercentileFromMedicalItemVitalSign(property, vitalSign);
    }

    private calculateAllPercentiles(vitalSign: MedicalItemVitalSign = null): void {
        this.mutateState(state => {
            this.calculatePercentile(state, "Height", vitalSign);
            this.calculatePercentile(state, "Weight", vitalSign);
            this.calculatePercentile(state, "MassBodyIndex", vitalSign);
            this.calculatePercentile(state, "HeadCircumference", vitalSign);
        });
    }

    private traverseMedicalSummarysNodes(node: ChildNode, searchText: string, isOverflowing: boolean, width: number): DOMRect[] {
        const arr = [];

        if (node && !IsNullOrWhiteSpace(searchText)) {
            const nodeElement = node as Element;
            const htmlElement = node as HTMLElement;
            const htmlElementStyle = htmlElement ? getComputedStyle(htmlElement) : null;

            isOverflowing = isOverflowing || htmlElementStyle?.overflow === "hidden";
            width = HighlightHelper.getWidth(htmlElement, isOverflowing, width);

            if (HighlightHelper.isBackgroundColorTransparent(htmlElementStyle?.backgroundColor)) {
                for (let i = 0; i < node?.childNodes?.length; i++) {
                    if (node.childNodes[i].nodeName === "#text") {
                        const textNode = node.childNodes[i] as Text;

                        if (textNode?.length > 0) {
                            arr.push(...HighlightHelper.getTextRectsToHighlightWithChildNode(nodeElement, textNode, searchText, width));
                        }
                    }
                    else {
                        arr.push(...this.traverseMedicalSummarysNodes(node.childNodes[i], searchText, isOverflowing, width));
                    }
                }
            }
        }

        return arr;
    }

    private initializeHighlightService(): void {
        this.highlightService = new HighlightService(
            this.medicalSummaryScrollerDivElement,
            this.medicalSummaryScrollerDivElement,
            this.medicalSummaryContainerClassName,
            this.props.model?.get("EncounterSearchTerm") ?? "",
            (searchText: string) => this.traverseMedicalSummarysNodes(this.medicalSummaryScrollerDivElement, searchText, false, HighlightHelper.invalidWidth)
        );

        this.highlightService?.initialize();
    }

    private executeHighlightService(searchText: string, isIndexReset: boolean): void {
        if (this.highlightService) {
            this.highlightService?.execute(searchText, isIndexReset);
            this.mutateState(s => s.allElementsToHighlight = [...this.highlightService.allElementsToHighlight]);
        }
    }

    private terminateHighlightService(): void {
        if (this.highlightService) {
            this.highlightService?.terminate();
            this.highlightService = null;
        }
    }

    private onLabRequestSignClick(requestModel: IMedicalRequest): void {
        const model = RequestsInboxItem.fromLabRequest(requestModel as LabRequest);
        ExportableFileDispatcher.openSignFaxPrintPopupForExamRequest({
            patientContactId: this.props.currentPatient.IndividualResponse.Contact_id,
            requestId: model.RequestId,
            requestDate: model.RequestDate,
            exportMode: ExportMode.Sign,
            isPortalSyncEnabled: false,
            signedPrintAuthorizations: model.SignedPrintAuthorizations,
            requestingStaffContactFacetId: model.RequestingStaffContactFacetId,
            createdByContactId: model.CreatedByContactId,
            onSignButtonCallback: () => {
                patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                    {
                        requestIds: this.props.model
                            .get("FollowUps")
                            .filter(p => p.MedicalRequestType === MedicalRequestType.LabRequest)
                            .map(p => p.Id)
                            .toArray()
                    }
                );
                this.closeRequestPopup();
            }
        });
    }

    private onConsultationSignClick(requestModel: IMedicalRequest): void {
        const model = ConsultationRequestsInboxItem.fromConsultationRequest(requestModel as ConsultationRequest);
        ExportableFileDispatcher.openSignFaxPrintPopupForConsultationRequest({
            patientContactId: this.props.currentPatient.IndividualResponse.Contact_id,
            consultationRequestId: model.ConsultationRequestId,
            consultationRequestDate: model.ConsultationRequestDate,
            exportMode: ExportMode.Sign,
            isPortalSyncEnabled: false,
            signedPrintAuthorizations: model.SignedPrintAuthorizations,
            requestingStaffContactFacetId: model.RequestingStaffContactFacetId,
            createdByContactId: model.CreatedByContactId,
            onSignButtonCallback: () => {
                patientStore.ActionsMedicalNote.LoadSignedAuthorizations(
                    {
                        consultationRequestIds: this.props.model
                            .get("FollowUps")
                            .filter(p => p.MedicalRequestType === MedicalRequestType.ConsultationRequest)
                            .map(p => p.Id)
                            .toArray()
                    }
                );
                this.closeRequestPopup();
            }
        });
    }
}

export { IMedicalSummaryProps, MedicalSummary };
